import { useState } from "react";
import { Modal } from "@/components/Modals";
import { Button } from "@/components/ui/button";
import { NOTE_MAX, cleanNote, noteHasPart, toggleNotePart } from "@/lib/itemNotes";

/**
 * The special note for ONE order line — "extra spicy" on the dal, not the roti.
 *
 * Opened by tapping the line (CaptainPAD while ordering or on the order summary,
 * the cart on the billing PC). The ready-made notes are chips that add or take out
 * their text; anything else is typed. Save hands the cleaned text to `onSave`,
 * which answers true once it is stored — the dialog stays open, with the reason
 * shown, when it is not.
 */
export default function ItemNoteDialog({ itemName, initial = "", presets = [], onSave, onClose }) {
  const [text, setText] = useState(initial || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async value => {
    setSaving(true);
    setError(null);
    try {
      const ok = await onSave(cleanNote(value));
      if (ok === false) return;
      onClose();
    } catch (failure) {
      setError(failure?.message || "The note was not saved");
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <div className="flex items-center justify-between gap-2 w-full">
      <Button
        type="button"
        variant="outline"
        className="h-11"
        onClick={() => save("")}
        disabled={saving || !cleanNote(initial)}
        data-testid="item-note-clear"
      >
        Remove note
      </Button>
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" className="h-11" onClick={onClose} disabled={saving} data-testid="item-note-cancel">
          Cancel
        </Button>
        <Button
          type="button"
          variant="action"
          className="h-11"
          onClick={() => save(text)}
          disabled={saving || cleanNote(text) === cleanNote(initial)}
          data-testid="item-note-save"
        >
          {saving ? "Saving…" : "Save note"}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal title={`Note for ${itemName}`} description="Printed on the KOT under this item." onClose={onClose} testId="item-note-dialog" footer={footer}>
      <div className="flex flex-col gap-3">
        {presets.length > 0 && (
          <div className="flex flex-wrap gap-2" role="group" aria-label="Ready-made notes" data-testid="item-note-presets">
            {presets.map(label => {
              const on = noteHasPart(text, label);
              return (
                <button
                  key={label}
                  type="button"
                  aria-pressed={on}
                  className={`h-11 px-4 rounded-full border text-body font-semibold transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "bg-card text-foreground hover:bg-muted"}`}
                  onClick={() => setText(current => toggleNotePart(current, label).slice(0, NOTE_MAX))}
                  data-testid={`item-note-preset-${label}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}
        <label className="flex flex-col gap-1 text-meta font-medium">
          <span>Note</span>
          <textarea
            className="w-full min-h-[5.5rem] rounded-md border bg-background p-3 text-body focus:ring-[3px] focus:ring-primary/15 focus:border-primary outline-none"
            value={text}
            maxLength={NOTE_MAX}
            onChange={e => setText(e.target.value)}
            placeholder="e.g. extra spicy, no onion"
            data-testid="item-note-text"
          />
          <span className="text-micro text-muted-foreground self-end tabular-nums">{text.length}/{NOTE_MAX}</span>
        </label>
        {error && (
          <p className="text-meta text-danger" role="alert" data-testid="item-note-error">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
